import type { FC, ReactNode } from 'react'
import type { LayoutProps } from '@/types'

import { useRef } from 'react'
import { useRouter } from 'next/router'

import { Navbar } from '@/components/generals/navbar'
import { Footer } from '@/components/generals/footer'
import { gsap, EASE, registerGsap, prefersReducedMotion, useIsomorphicLayoutEffect } from '@/lib/motion'

export const Layout: FC<LayoutProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const mainRef = useRef<HTMLElement | null>(null)

    /**
     * Page transition: each route's content resolves from a slight blur and
     * lift, so navigating feels continuous rather than a hard swap.
     */
    useIsomorphicLayoutEffect(() => {
        const node = mainRef.current

        if (!node || prefersReducedMotion()) {
            return
        }

        registerGsap()

        const context = gsap.context(() => {
            gsap.fromTo(
                node,
                { opacity: 0, y: 12, filter: 'blur(8px)' },
                {
                    opacity: 1,
                    y: 0,
                    filter: 'blur(0px)',
                    duration: 0.7,
                    ease: EASE.enter,
                    clearProps: 'filter,transform'
                }
            )
        }, node)

        return () => context.revert()
    }, [router.asPath])

    return (
        <div className='flex min-h-screen flex-col bg-background'>
            <a
                href='#main'
                className='sr-only rounded-lg bg-white px-4 py-2 text-sm font-medium text-black focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-100'
            >
                Skip to content
            </a>

            <Navbar />

            <main
                id='main'
                ref={mainRef}
                className='flex-1'
            >
                {children}
            </main>

            <Footer />
        </div>
    )
}
